import {
    createFeatureCommandSeq,
    createFeatureReplayResultHash,
} from '../../feature/rebuild/FeatureReplayConsistency';
import type {FeaturePrototype} from '../../feature/prototype/FeaturePrototype';
import {SketchDisplayState} from '../../viewer/moudles/SketchGroupManager';
import {
    createFeatureDefinitionHistorySnapshotFromCommand,
    type FeatureDefinitionHistorySnapshot,
} from '../domain/FeatureHistoryAdapter';
import {
    createExistingHistorySnapshot,
    createMissingHistorySnapshot,
    type HistoryEntitySnapshot,
} from '../types';

/**
 * Feature 历史提交记录。
 */
export interface EditorFeatureHistoryRecord {
    /** Feature ID。 */
    readonly featureId: string;
    /** Feature 类型。 */
    readonly featureKind: string;
    /** 用户可读标签。 */
    readonly label: string;
    /** 历史命令类型。 */
    readonly commandType: string;
    /** 被 feature 消费的源草图 ID。 */
    readonly sourceSketchId?: string | null;
    /** 编辑已有 feature 时，进入编辑会话前捕获的 before 主状态基线。 */
    readonly editBaseline?: EditorFeatureEditBaselineSnapshot | null;
    /** feature 提交前源草图显示态。 */
    readonly sourceSketchDisplayStateBefore?: SketchDisplayState | null;
    /** feature 提交后源草图显示态。 */
    readonly sourceSketchDisplayStateAfter?: SketchDisplayState | null;
    /** 提交前 feature 主状态。 */
    readonly before: HistoryEntitySnapshot<FeatureDefinitionHistorySnapshot>;
    /** 提交后 feature 主状态。 */
    readonly after: HistoryEntitySnapshot<FeatureDefinitionHistorySnapshot>;
    /** 受影响的渲染对象 ID。 */
    readonly renderObjectIds: readonly string[];
}

/**
 * Feature 编辑会话进入时的原始命令基线。
 */
export interface EditorFeatureEditBaselineSnapshot {
    /** 正在编辑的 Feature ID。 */
    readonly featureId: string;
    /** 进入编辑会话前的目标 feature command。 */
    readonly command: CadCommand;
    /** 目标命令在原始 feature history 中的位置。 */
    readonly commandIndex?: number;
    /** 进入编辑会话前完整 feature command 序列的哈希。 */
    readonly commandSeq?: string;
}

/**
 * 带有源草图读取能力的 feature 数据。
 */
interface FeatureSourceSketchReadable {
    /** 读取源草图 ID。 */
    readonly getSourceSketchId: () => string | null;
}

/**
 * Feature 提交结果最小结构。
 */
interface FeatureCommitResultLike {
    /** Feature ID。 */
    readonly featureId: string;
    /** 单结果体对象 ID。 */
    readonly objectId?: string;
    /** 多结果体对象 ID。 */
    readonly objectIds?: readonly string[];
    /** 已提交的 feature 原型，只读取稳定标识，不写入历史。 */
    readonly prototype?: FeaturePrototype;
}

/**
 * Feature 历史捕获所需 Viewer 端口。
 */
interface FeatureHistoryViewerPort {
    /** 读取 Worker 权威几何状态。 */
    readonly getGeometryStateSummary: () => Promise<WorkerGeometryStateSummary>;
    /** 读取草图显示态。 */
    readonly getSketchGroupDisplayState?: (sketchId: string) => SketchDisplayState | null;
    /** 读取特征结果体隐藏态。 */
    readonly isFeatureResultHidden?: (featureId: string) => boolean;
    /** 回滚 feature 命令历史。 */
    readonly replayFeatureHistoryCommands?: (
        commands: readonly CadCommand[],
        options?: {
            /** replay 失败时用于恢复 Worker 的命令历史。 */
            readonly rollbackCommands?: readonly CadCommand[];
            /** replay 前的 Worker 摘要；由调用方已持有快照时传入，避免重复读取造成时间线重入。 */
            readonly beforeSummary?: WorkerGeometryStateSummary;
        },
    ) => Promise<WorkerGeometryStateSummary>;
}

/**
 * Feature 提交历史捕获输入。
 */
export interface ExecuteFeatureCommitWithHistoryInput<TResult extends FeatureCommitResultLike> {
    /** 当前 Viewer。 */
    readonly viewer: FeatureHistoryViewerPort;
    /** 编辑已有 feature 时的目标 ID。 */
    readonly featureId?: string | null;
    /** 用户可读标签。 */
    readonly label: string;
    /** 历史命令类型。 */
    readonly commandType: string;
    /** 当前 feature 定义，用于在 commit 前读取源草图 ID。 */
    readonly definition?: unknown;
    /** 编辑已有 feature 时进入编辑会话前的 before 主状态基线。 */
    readonly editBaseline?: EditorFeatureEditBaselineSnapshot | null;
    /** 调用方已解析的源草图 ID。 */
    readonly sourceSketchId?: string | null;
    /** 实际 feature 提交函数。 */
    readonly commit: () => Promise<TResult>;
    /** 写入模型历史层的回调；未提供时只执行原提交。 */
    readonly commitFeatureHistoryRecord?: (record: EditorFeatureHistoryRecord) => Promise<void> | void;
}

/**
 * 判断对象是否能读取源草图 ID。
 *
 * @param value 待判断对象。
 * @returns 是否具备源草图读取能力。
 */
function isFeatureSourceSketchReadable(value: unknown): value is FeatureSourceSketchReadable {
    return typeof value === 'object'
        && value !== null
        && 'getSourceSketchId' in value
        && typeof (value as { getSourceSketchId?: unknown }).getSourceSketchId === 'function';
}

/**
 * 规范化源草图 ID。
 *
 * @param sketchId 原始草图 ID。
 * @returns 有效草图 ID；无效时返回 null。
 */
function normalizeSourceSketchId(sketchId: string | null | undefined): string | null {
    if (typeof sketchId !== 'string') {
        return null;
    }

    const trimmedSketchId = sketchId.trim();
    return trimmedSketchId.length > 0 ? trimmedSketchId : null;
}

/**
 * 从 feature 定义或原型读取源草图 ID。
 *
 * @param source feature 定义或原型。
 * @returns 源草图 ID；不存在时返回 null。
 */
function resolveSourceSketchIdFromFeatureSource(source: unknown): string | null {
    if (!isFeatureSourceSketchReadable(source)) {
        return null;
    }

    return normalizeSourceSketchId(source.getSourceSketchId());
}

/**
 * 从历史快照参数兜底读取源草图 ID。
 *
 * @param snapshot feature 历史快照。
 * @returns 源草图 ID；不存在时返回 null。
 */
function resolveSourceSketchIdFromFeatureSnapshot(
    snapshot: HistoryEntitySnapshot<FeatureDefinitionHistorySnapshot>,
): string | null {
    if (!snapshot.exists || !snapshot.data) {
        return null;
    }

    const sketchId = snapshot.data.parameters.sketchId;
    return typeof sketchId === 'string' ? normalizeSourceSketchId(sketchId) : null;
}

/**
 * 读取源草图显示态。
 *
 * @param viewer Viewer 历史端口。
 * @param sourceSketchId 源草图 ID。
 * @returns 当前显示态；不可读取时返回 null。
 */
function readSourceSketchDisplayState(
    viewer: FeatureHistoryViewerPort,
    sourceSketchId: string | null,
): SketchDisplayState | null {
    if (!sourceSketchId || !viewer.getSketchGroupDisplayState) {
        return null;
    }

    return viewer.getSketchGroupDisplayState(sourceSketchId);
}

/**
 * 从 Worker 摘要捕获 feature 主状态快照。
 *
 * @param summary Worker 几何状态摘要。
 * @param featureId Feature ID。
 * @returns feature 主状态快照。
 */
export function captureFeatureHistorySnapshotFromSummary(
    summary: WorkerGeometryStateSummary,
    featureId: string | null | undefined,
    options: {
        /** 是否隐藏。 */
        readonly hidden?: boolean;
    } = {},
): HistoryEntitySnapshot<FeatureDefinitionHistorySnapshot> {
    if (!featureId) {
        return createMissingHistorySnapshot();
    }

    const commandIndex = summary.history.findIndex((command) => command.id === featureId);
    const command = commandIndex >= 0 ? summary.history[commandIndex] : null;

    if (!command) {
        return createMissingHistorySnapshot();
    }

    return createExistingHistorySnapshot(createFeatureDefinitionHistorySnapshotFromCommand({
        command,
        commandIndex,
        commandSeq: createFeatureCommandSeq(summary.history),
        resultHash: createFeatureReplayResultHash(summary),
        hidden: options.hidden,
    }));
}

/**
 * 从编辑会话基线捕获 feature before 快照。
 *
 * @param featureId 当前提交目标 feature ID。
 * @param baseline 进入编辑会话时记录的原始命令基线。
 * @returns 可用于 EntityPatch before 的 feature 快照；没有匹配基线时返回 null。
 */
function captureFeatureHistorySnapshotFromEditBaseline(
    featureId: string | null | undefined,
    baseline: EditorFeatureEditBaselineSnapshot | null | undefined,
    options: {
        /** 是否隐藏。 */
        readonly hidden?: boolean;
    } = {},
): HistoryEntitySnapshot<FeatureDefinitionHistorySnapshot> | null {
    if (!featureId || !baseline || baseline.featureId !== featureId) {
        return null;
    }

    if (baseline.command.id !== baseline.featureId) {
        throw new Error(`Feature 编辑基线不一致：baseline=${baseline.featureId}, command=${baseline.command.id}`);
    }

    return createExistingHistorySnapshot(createFeatureDefinitionHistorySnapshotFromCommand({
        command: baseline.command,
        commandIndex: baseline.commandIndex,
        commandSeq: baseline.commandSeq,
        hidden: options.hidden,
    }));
}

/**
 * 解析 feature 提交影响到的渲染对象 ID。
 *
 * @param summary Worker 几何状态摘要。
 * @param featureId Feature ID。
 * @param result Feature 提交结果。
 * @returns 去重后的渲染对象 ID。
 */
function resolveFeatureRenderObjectIds(
    summary: WorkerGeometryStateSummary,
    featureId: string,
    result: FeatureCommitResultLike,
): readonly string[] {
    const objectIds = new Set<string>();

    if (result.objectId) {
        objectIds.add(result.objectId);
    }

    result.objectIds?.forEach((objectId) => {
        objectIds.add(objectId);
    });

    summary.objects.forEach((object) => {
        const metadata = object.metadata as Record<string, unknown>;
        if (object.commandId === featureId || metadata.featureId === featureId) {
            objectIds.add(object.id);
        }
    });

    return Array.from(objectIds);
}

/**
 * 回滚已经提交到 Worker 的 feature 变更。
 *
 * @param viewer Viewer 历史端口。
 * @param beforeCommands 提交前命令历史。
 * @param afterCommands 提交后命令历史。
 */
async function rollbackFeatureCommitAfterHistoryFailure(
    viewer: FeatureHistoryViewerPort,
    beforeSummary: WorkerGeometryStateSummary,
    beforeCommands: readonly CadCommand[],
    afterCommands: readonly CadCommand[],
): Promise<void> {
    if (!viewer.replayFeatureHistoryCommands) {
        throw new Error('Feature 历史提交失败，但 Viewer 未提供 feature 回滚入口。');
    }

    await viewer.replayFeatureHistoryCommands(beforeCommands, {
        rollbackCommands: afterCommands,
        beforeSummary,
    });
}

/**
 * 执行 feature commit 并捕获 Undo/Redo 所需主状态。
 *
 * @param input 执行输入。
 * @returns 原始 feature 提交结果。
 * @throws 当无法捕获完整的 after 快照时抛出错误。
 * @throws 原样抛出主状态读取、特征提交或历史记录异常；若历史记录失败后的回滚也失败，则抛出回滚异常。
 */
export async function executeFeatureCommitWithHistory<TResult extends FeatureCommitResultLike>(
    input: ExecuteFeatureCommitWithHistoryInput<TResult>,
): Promise<TResult> {
    if (!input.commitFeatureHistoryRecord) {
        return input.commit();
    }

    const beforeSummary = await input.viewer.getGeometryStateSummary();
    const beforeFeatureHidden = input.featureId
        ? input.viewer.isFeatureResultHidden?.(input.featureId)
        : undefined;
    const beforeSnapshot = captureFeatureHistorySnapshotFromEditBaseline(input.featureId, input.editBaseline, {
        hidden: beforeFeatureHidden,
    })
        ?? captureFeatureHistorySnapshotFromSummary(beforeSummary, input.featureId, {
            hidden: beforeFeatureHidden,
        });
    const sourceSketchIdBeforeCommit = normalizeSourceSketchId(input.sourceSketchId)
        ?? resolveSourceSketchIdFromFeatureSource(input.definition)
        ?? resolveSourceSketchIdFromFeatureSnapshot(beforeSnapshot);
    const sourceSketchDisplayStateBefore = readSourceSketchDisplayState(
        input.viewer,
        sourceSketchIdBeforeCommit,
    );
    const result = await input.commit();
    const afterSummary = await input.viewer.getGeometryStateSummary();

    try {
        const afterSnapshot = captureFeatureHistorySnapshotFromSummary(afterSummary, result.featureId, {
            hidden: input.viewer.isFeatureResultHidden?.(result.featureId),
        });

        if (!afterSnapshot.exists) {
            throw new Error(`特征 ${result.featureId} 提交成功，但无法从 Worker 命令历史捕获 after 快照。`);
        }

        const afterData = afterSnapshot.data;
        if (!afterData) {
            throw new Error(`特征 ${result.featureId} 的 after 快照缺少主状态数据。`);
        }

        const sourceSketchId = sourceSketchIdBeforeCommit
            ?? resolveSourceSketchIdFromFeatureSource(result.prototype)
            ?? resolveSourceSketchIdFromFeatureSnapshot(afterSnapshot);
        const sourceSketchDisplayStateAfter = readSourceSketchDisplayState(input.viewer, sourceSketchId);

        await input.commitFeatureHistoryRecord({
            featureId: result.featureId,
            featureKind: afterData.featureKind,
            label: input.label,
            commandType: input.commandType,
            sourceSketchId,
            sourceSketchDisplayStateBefore,
            sourceSketchDisplayStateAfter,
            before: beforeSnapshot,
            after: afterSnapshot,
            renderObjectIds: resolveFeatureRenderObjectIds(afterSummary, result.featureId, result),
        });
    } catch (error) {
        await rollbackFeatureCommitAfterHistoryFailure(
            input.viewer,
            beforeSummary,
            beforeSummary.history,
            afterSummary.history,
        );
        throw error;
    }

    return result;
}
